/**
 * Auto-Repair Historical Dashboard Component
 * 
 * Displays historical trends and metrics for the auto-repair system
 * using interactive charts and visualizations.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  TrendingUp, RefreshCcw, Calendar, Activity, BarChart3,
  Bot, AlertTriangle, RotateCcw, Brain
} from "lucide-react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { CHART_COLORS, formatDate } from "./constants";

function CustomTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
        <p className="font-medium text-sm mb-2">{formatDate(label)}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

export function AutoRepairHistoryDashboard({ history, period, onPeriodChange, onRefresh }) {
  if (!history) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <RotateCcw className="w-8 h-8 mx-auto animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Carregando dados historicos...</p>
        </div>
      </div>
    );
  }

  const { timeline, by_agent, by_error_type, totals } = history;

  return (
    <div className="space-y-6" data-testid="auto-repair-history-dashboard">
      {/* Header with Period Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-500/15 rounded-lg">
            <TrendingUp className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Dashboard Historico</h2>
            <p className="text-sm text-muted-foreground">
              Tendencias e metricas de auto-repair ao longo do tempo
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(period)} onValueChange={(v) => onPeriodChange(Number(v))}>
            <SelectTrigger className="w-32" data-testid="period-selector">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="14">14 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-history">
            <RefreshCcw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-gradient-to-br from-blue-500/10 to-transparent border-blue-500/20">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-blue-400" data-testid="total-detected">
              {totals?.total || 0}
            </p>
            <p className="text-xs text-muted-foreground">Total Detectados</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-green-400" data-testid="total-successes">
              {totals?.successes || 0}
            </p>
            <p className="text-xs text-muted-foreground">Corrigidos</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-red-400" data-testid="total-failures">
              {totals?.failures || 0}
            </p>
            <p className="text-xs text-muted-foreground">Falhas</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-amber-400" data-testid="total-escalated">
              {totals?.escalated || 0}
            </p>
            <p className="text-xs text-muted-foreground">Escalados</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/20">
          <CardContent className="pt-4 text-center">
            <p className="text-3xl font-bold text-emerald-400" data-testid="avg-success-rate">
              {totals?.success_rate || 0}%
            </p>
            <p className="text-xs text-muted-foreground">Taxa Media</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Timeline Chart */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Tendencia de Auto-Repair
          </CardTitle>
          <CardDescription>
            Problemas detectados vs corrigidos ao longo do periodo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {timeline && timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={timeline} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.total} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_COLORS.total} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis stroke="#666" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="total" 
                  name="Detectados"
                  stroke={CHART_COLORS.total} 
                  fillOpacity={1} 
                  fill="url(#colorTotal)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="successes" 
                  name="Corrigidos"
                  stroke={CHART_COLORS.success} 
                  fillOpacity={1} 
                  fill="url(#colorSuccess)" 
                />
                <Line 
                  type="monotone" 
                  dataKey="failures" 
                  name="Falhas"
                  stroke={CHART_COLORS.failure} 
                  strokeWidth={2}
                  dot={{ fill: CHART_COLORS.failure }}
                />
                <Line 
                  type="monotone" 
                  dataKey="escalated" 
                  name="Escalados"
                  stroke={CHART_COLORS.escalated} 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: CHART_COLORS.escalated }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum dado disponivel para o periodo selecionado</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Success Rate Over Time */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            Taxa de Sucesso ao Longo do Tempo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {timeline && timeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={timeline} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  stroke="#666"
                  fontSize={12}
                />
                <YAxis stroke="#666" fontSize={12} domain={[0, 100]} unit="%" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="success_rate" 
                  name="Taxa de Sucesso"
                  stroke={CHART_COLORS.successRate} 
                  strokeWidth={3}
                  dot={{ fill: CHART_COLORS.successRate, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">
              Nenhum dado disponivel
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Performance by Agent */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-400" />
              Desempenho por Agente
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_agent && by_agent.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={by_agent} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis type="number" stroke="#666" fontSize={12} />
                  <YAxis type="category" dataKey="agent" stroke="#666" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="successes" name="Sucesso" stackId="a" fill={CHART_COLORS.success} />
                  <Bar dataKey="failures" name="Falha" stackId="a" fill={CHART_COLORS.failure} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Nenhum dado por agente disponivel
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Error Types */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Tipos de Erro Mais Frequentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {by_error_type && by_error_type.length > 0 ? (
              <div className="space-y-3">
                {by_error_type.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/20">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-mono text-muted-foreground">#{idx + 1}</span>
                      <div>
                        <p className="text-sm font-medium">{item.error_type}</p>
                        <p className="text-xs text-muted-foreground">{item.total} ocorrencias</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge 
                        className={item.success_rate >= 70 
                          ? "bg-green-500/15 text-green-400" 
                          : item.success_rate >= 40 
                            ? "bg-amber-500/15 text-amber-400"
                            : "bg-red-500/15 text-red-400"
                        }
                      >
                        {item.success_rate}% sucesso
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                Nenhum erro registrado no periodo
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Insights Card */}
      {totals && totals.total > 0 && (
        <Card className="bg-gradient-to-br from-purple-500/5 to-transparent border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-500/15 rounded-xl">
                <Brain className="w-8 h-8 text-purple-400" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Insights do Periodo</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  {totals.success_rate >= 80 && (
                    <p className="text-green-400">
                      Excelente! Taxa de auto-resolucao acima de 80%. O sistema esta funcionando muito bem.
                    </p>
                  )}
                  {totals.success_rate >= 50 && totals.success_rate < 80 && (
                    <p className="text-amber-400">
                      Taxa de auto-resolucao em {totals.success_rate}%. Considere revisar as regras com mais falhas.
                    </p>
                  )}
                  {totals.success_rate < 50 && (
                    <p className="text-red-400">
                      Taxa de auto-resolucao abaixo de 50%. Recomenda-se investigar os tipos de erro mais frequentes.
                    </p>
                  )}
                  {totals.escalated > totals.successes && (
                    <p className="text-amber-400">
                      Muitos problemas sendo escalados ({totals.escalated}). Considere adicionar novas regras de auto-repair.
                    </p>
                  )}
                  {by_agent && by_agent.length > 0 && (
                    <p>
                      Agente mais ativo: <strong>{by_agent[0]?.agent}</strong> com {by_agent[0]?.total} acoes.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
